import type { FC } from 'react';

import Avatar from '@/common-components/Avatar';
import type { CampusFilterOption, StudentListItem } from '@/types/student.types';

import { AccountStatusPill, KycStatusPill } from './StatusPills';
import TrustScoreBar from './TrustScoreBar';
import UserActions from './UserActions';

interface UsersTableProps {
  users: StudentListItem[];
  campusNameById: Record<string, string>;
  suspendingId: string | null;
  onView: (user: StudentListItem) => void;
  onSuspend: (user: StudentListItem) => void;
  onActivate: (user: StudentListItem) => void;
}

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

/**
 * On the Deleted tab the user document holds only `Deleted user` and a `.invalid` address —
 * the real identity comes from the deletion snapshot the server attaches there.
 */
const identityOf = (user: StudentListItem): { name: string; email: string } =>
  user.deletedIdentity
    ? { name: user.deletedIdentity.name, email: user.deletedIdentity.collegeEmail }
    : { name: user.name, email: user.collegeEmail };

const campusOf = (
  campusId: string | null,
  campusNameById: Record<string, string>,
): string => {
  if (!campusId) return '—';
  return campusNameById[campusId] ?? '—';
};

const UsersTable: FC<UsersTableProps> = ({
  users,
  campusNameById,
  suspendingId,
  onView,
  onSuspend,
  onActivate,
}) => (
  <div className="hidden overflow-x-auto md:block">
    <table className="w-full border-collapse">
      <thead>
        <tr className="bg-[var(--color-surface-muted)]">
          {['User', 'Campus', 'Trust Score', 'KYC Status', 'Account', 'Rentals', 'Joined', 'Actions'].map(
            (h) => (
              <th
                key={h}
                className="border-b border-[var(--color-divider)] px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-[var(--color-ink-muted)]"
              >
                {h}
              </th>
            ),
          )}
        </tr>
      </thead>
      <tbody>
        {users.map((user) => (
          <tr
            key={user.id}
            className="border-b border-[var(--color-divider)] transition-colors hover:bg-[var(--color-surface-muted)] last:border-b-0"
          >
            <td className="px-4 py-3">
              <div className="flex items-center gap-3">
                <Avatar name={identityOf(user).name} src={user.avatarUrl} size="md" />
                <div className="min-w-0">
                  <div className="truncate text-[13px] font-semibold text-[var(--color-ink)]">
                    {identityOf(user).name}
                  </div>
                  <div className="truncate text-[11px] text-[var(--color-ink-soft)]">
                    {identityOf(user).email}
                  </div>
                  {user.deletedIdentity?.mobile ? (
                    <div className="truncate text-[11px] text-[var(--color-ink-soft)]">
                      {user.deletedIdentity.mobile}
                    </div>
                  ) : null}
                </div>
              </div>
            </td>
            <td className="px-4 py-3 text-[12.5px] text-[var(--color-ink)]">
              {campusOf(user.campusId, campusNameById)}
            </td>
            <td className="px-4 py-3">
              <TrustScoreBar value={user.trustScore} />
            </td>
            <td className="px-4 py-3">
              <KycStatusPill status={user.kycStatus} accountStatus={user.accountStatus} />
            </td>
            <td className="px-4 py-3">
              <AccountStatusPill status={user.accountStatus} />
            </td>
            <td className="px-4 py-3 text-[13px]">
              <span className="font-bold text-[var(--color-ink)]">{user.rentalsCompleted}</span>
              <span className="ml-1 text-[11px] text-[var(--color-ink-soft)]">rentals</span>
            </td>
            <td className="px-4 py-3 text-[12px] text-[var(--color-ink-muted)]">
              {formatDate(user.createdAt)}
              {user.deletedAt ? (
                <div className="text-[11px] text-[var(--color-ink-soft)]">
                  Deleted {formatDate(user.deletedAt)}
                </div>
              ) : null}
            </td>
            <td className="px-4 py-3">
              <UserActions
                user={user}
                suspendingId={suspendingId}
                onView={onView}
                onSuspend={onSuspend}
                onActivate={onActivate}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default UsersTable;

interface UsersCardListProps extends UsersTableProps {
  campuses: CampusFilterOption[];
}

/**
 * Mobile (<768px) representation of the same rows as stacked cards. Carries the same data and
 * action callbacks as the desktop table for layout symmetry.
 */
export const UsersCardList: FC<UsersCardListProps> = ({
  users,
  campusNameById,
  suspendingId,
  onView,
  onSuspend,
  onActivate,
}) => (
  <div className="flex flex-col gap-3 p-3 md:hidden">
    {users.map((user) => (
      <article
        key={user.id}
        className="rounded-xl border border-[var(--color-divider)] bg-white p-4 shadow-[var(--shadow-card)]"
      >
        <header className="flex items-start gap-3">
          <Avatar name={identityOf(user).name} src={user.avatarUrl} size="lg" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-[var(--color-ink)]">
              {identityOf(user).name}
            </div>
            <div className="truncate text-xs text-[var(--color-ink-soft)]">
              {identityOf(user).email}
            </div>
            <div className="mt-1 text-[11px] text-[var(--color-ink-muted)]">
              {campusOf(user.campusId, campusNameById)} · Joined {formatDate(user.createdAt)}
              {user.deletedAt ? ` · Deleted ${formatDate(user.deletedAt)}` : ''}
            </div>
          </div>
        </header>

        <div className="mt-3 grid grid-cols-2 gap-3 border-t border-[var(--color-divider)] pt-3">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-ink-soft)]">
              Trust Score
            </div>
            <div className="mt-1">
              <TrustScoreBar value={user.trustScore} />
            </div>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-ink-soft)]">
              Rentals
            </div>
            <div className="mt-1 text-sm font-bold text-[var(--color-ink)]">
              {user.rentalsCompleted}
              <span className="ml-1 text-[10px] font-normal text-[var(--color-ink-soft)]">
                completed
              </span>
            </div>
          </div>
          <div className="col-span-2 flex flex-wrap gap-2">
            <KycStatusPill status={user.kycStatus} accountStatus={user.accountStatus} />
            <AccountStatusPill status={user.accountStatus} />
          </div>
        </div>

        <footer className="mt-3 border-t border-[var(--color-divider)] pt-3">
          <UserActions
            user={user}
            suspendingId={suspendingId}
            onView={onView}
            onSuspend={onSuspend}
            onActivate={onActivate}
          />
        </footer>
      </article>
    ))}
  </div>
);
